import { graphql } from 'gql.tada';
import { useMutation } from '@apollo/client';
import { useCallback } from 'react';

const GetEnrollmentPacketMutation = graphql.persisted(
  "GetEnrollmentPacket",
  graphql(`
    mutation GetEnrollmentPacket($token: String!) {
      getEnrollmentPacket(input: {token: $token}) {
        familyEnrollment { id state isLocked family { id name } }
        formMetadata
        formAnswers
        paymentSettings
        packetStudents
        invoiceSummary
        policyDocuments
        familyName
        schoolYearName
        userErrors { message path }
      }
    }
  `)
);

const SaveEnrollmentPacketDraftMutation = graphql.persisted(
  "SaveEnrollmentPacketDraft",
  graphql(`
    mutation SaveEnrollmentPacketDraft($token: String!, $formData: JSON!, $currentStep: Int) {
      saveEnrollmentPacketDraft(input: {token: $token, formData: $formData, currentStep: $currentStep}) {
        success formData
        userErrors { message path }
      }
    }
  `)
);

const SubmitEnrollmentPacketMutation = graphql.persisted(
  "SubmitEnrollmentPacket",
  graphql(`
    mutation SubmitEnrollmentPacket($token: String!, $formData: JSON!, $paymentMethod: String) {
      submitEnrollmentPacket(input: {token: $token, formData: $formData, paymentMethod: $paymentMethod}) {
        familyEnrollment { id state packetSubmittedAt }
        familyEnrollmentId
        enrollmentFeeInvoiceId
        requiresPayment
        checkoutUrl
        userErrors { message path }
      }
    }
  `)
);

const VerifyCheckoutSessionMutation = graphql.persisted(
  "VerifyCheckoutSession",
  graphql(`
    mutation VerifyCheckoutSession($token: String!, $sessionId: String!) {
      verifyCheckoutSession(input: { token: $token, sessionId: $sessionId }) {
        familyEnrollment { id isLocked }
        paymentStatus checkoutStatus checkoutUrl checkoutPaymentMethod subscriptionStatus
        userErrors { message path }
      }
    }
  `)
);

const CreateEnrollmentPacketCheckoutSessionMutation = graphql.persisted(
  "CreateEnrollmentPacketCheckoutSession",
  graphql(`
    mutation CreateEnrollmentPacketCheckoutSession($token: String!, $paymentMethod: String) {
      createEnrollmentPacketCheckoutSession(input: { token: $token, paymentMethod: $paymentMethod }) {
        checkoutUrl
        userErrors { message path }
      }
    }
  `)
);

export {
  GetEnrollmentPacketMutation,
  SaveEnrollmentPacketDraftMutation,
  SubmitEnrollmentPacketMutation,
  VerifyCheckoutSessionMutation,
  CreateEnrollmentPacketCheckoutSessionMutation,
};

/**
 * Hook to fetch enrollment packet data
 */
export function useGetEnrollmentPacket(token: string) {
  const [getPacket, { loading, error, data }] = useMutation(GetEnrollmentPacketMutation);

  const fetchPacket = useCallback(async () => {
    const result = await getPacket({ variables: { token } });
    return result.data?.getEnrollmentPacket;
  }, [getPacket, token]);

  return { fetchPacket, loading, error, data };
}

/**
 * Hook to save enrollment packet draft
 */
export function useSaveEnrollmentDraft(token: string) {
  const [saveDraft, { loading, error }] = useMutation(SaveEnrollmentPacketDraftMutation);

  const save = useCallback(async (formData: any, currentStep: number) => {
    await saveDraft({
      variables: { token, formData, currentStep },
    });
  }, [saveDraft, token]);

  return { save, loading, error };
}

/**
 * Hook to submit enrollment packet
 */
export function useSubmitEnrollmentPacket(token: string) {
  const [submitPacket, { loading, error }] = useMutation(SubmitEnrollmentPacketMutation);

  const submit = useCallback(async (formData: any, paymentMethod: string) => {
    const result = await submitPacket({
      variables: { token, formData, paymentMethod },
    });
    return result.data?.submitEnrollmentPacket;
  }, [submitPacket, token]);

  return { submit, loading, error };
}

/**
 * Hook to verify checkout session
 */
export function useVerifyCheckout(token: string) {
  const [verifyCheckout, { loading, error }] = useMutation(VerifyCheckoutSessionMutation);

  const verify = useCallback(async (sessionId: string) => {
    if (!sessionId) return null;

    const result = await verifyCheckout({
      variables: { token, sessionId },
    });
    return result.data?.verifyCheckoutSession;
  }, [verifyCheckout, token]);

  return { verify, loading, error };
}

/**
 * Hook to create new checkout session
 */
export function useCreateCheckoutSession(token: string) {
  const [createCheckoutSession, { loading, error }] = useMutation(
    CreateEnrollmentPacketCheckoutSessionMutation
  );

  const createSession = useCallback(async (paymentMethod: string) => {
    const result = await createCheckoutSession({
      variables: { token, paymentMethod },
    });
    return result.data?.createEnrollmentPacketCheckoutSession;
  }, [createCheckoutSession, token]);

  return { createSession, loading, error };
}
